import {useAuth} from '@apps-common/auth-react';
import {useGetRestrictions} from '@src/api/dataCollections/accessControl.ts';
import {useGetIMSUserStatus} from '@src/api/ims.ts';
import {notifyError} from '@src/components/notifier/notify';
import {OUT_OF_PUS_RESTRICTION, RoleLevel} from '@src/constants/account.ts';
import {DateTime} from 'luxon';
import {useEffect} from 'react';

const usePlatformAccess = () => {
  const auth = useAuth();

  const platformQuery = useGetIMSUserStatus();

  const accountId = auth?.user?.claims?.pl_customer_account as string;
  const restrictionsQuery = useGetRestrictions(accountId, {
    enabled: !!accountId,
  });

  const isLoading = platformQuery.isLoading ?? restrictionsQuery.isLoading;
  const error = platformQuery.error ?? restrictionsQuery.error;

  useEffect(() => {
    if (error) {
      notifyError({data: {message: 'Error loading user permissions'}});
    }
  }, [error]);

  const platformAccess = platformQuery.data?.platformAccess;
  const hasPlatformAccess = platformQuery.error ? false : !!platformAccess;
  const accountIsManaged = !!platformAccess?.isManaged;
  const accountIsAdmin =
    auth?.user?.claims?.pl_project_role === RoleLevel.ORGANIZATION_ADMIN;
  const accountHasExpired = platformAccess?.expiresAt
    ? DateTime.fromISO(platformAccess.expiresAt) < DateTime.now()
    : false;
  const restrictions =
    !error && !isLoading ? (restrictionsQuery.data?.restrictions ?? []) : [];
  const accountOutOfPUs = !!restrictions.includes(OUT_OF_PUS_RESTRICTION);

  return {
    accountIsManaged,
    accountIsAdmin,
    accountHasExpired,
    accountOutOfPUs,
    dataCollections: {
      hasReadOnlyAccess: restrictions.length > 0 || accountHasExpired,
      serviceUnavailable: hasPlatformAccess && !!restrictionsQuery.error,
    },
    error,
    hasPlatformAccess,
    isLoading,
  };
};

export default usePlatformAccess;
